import { NavLink } from 'react-router';
import { useSessao } from '../sessao/SessaoProvedor';

/**
 * Trilho lateral de navegação.
 *
 * Escuro, com a marca no topo — é a única superfície do sistema que usa o preto da
 * identidade em cheio. O conteúdo fica em papel claro porque tabela densa lida oito
 * horas por dia se lê melhor assim; o preto fica onde ele assina, não onde ele
 * atrapalha.
 *
 * Os grupos seguem a ORDEM DO DIA da colaboradora: primeiro o que fazer agora,
 * depois o dinheiro, depois as clínicas, e por último o que se mexe de vez em
 * quando. Não é uma lista alfabética nem um espelho dos módulos do sistema.
 */

interface Item {
  readonly para: string;
  readonly rotulo: string;
  /** Contagem que aparece como selo. `undefined` não desenha selo nenhum. */
  readonly contagem?: number | undefined;
  /** Selo em tom de alerta, quando o número representa pendência. */
  readonly alerta?: boolean;
}

interface Grupo {
  readonly titulo: string;
  readonly itens: readonly Item[];
}

export function Trilho({ contagens }: { contagens: Record<string, number> }) {
  const { usuaria, sair } = useSessao();

  const grupos: readonly Grupo[] = [
    {
      titulo: 'Operação',
      itens: [
        { para: '/painel', rotulo: 'Painel geral' },
        {
          para: '/cobrancas',
          rotulo: 'Cobranças',
          contagem: contagens['fila'],
          alerta: true,
        },
        { para: '/devedores', rotulo: 'Carteira de devedores' },
        {
          para: '/triagem',
          rotulo: 'Triagem judicial',
          contagem: contagens['triagem'],
        },
      ],
    },
    {
      titulo: 'Financeiro',
      itens: [
        { para: '/pagamentos', rotulo: 'Pagamentos recebidos' },
        { para: '/fechamentos', rotulo: 'Honorários e fechamento' },
      ],
    },
    {
      // "Empresa" só no menu, por ora. A carteira continua sendo de clínicas no
      // resto do sistema — e o endereço `/clinicas` não muda, para não derrubar
      // link que alguém já guardou nos favoritos.
      titulo: 'Empresas',
      itens: [
        { para: '/clinicas', rotulo: 'Empresas e portal' },
        { para: '/relatorios', rotulo: 'Relatórios mensais' },
      ],
    },
    {
      titulo: 'Ferramentas',
      itens: [
        { para: '/calculadora', rotulo: 'Calculadora de débito' },
        { para: '/importar', rotulo: 'Importar planilha' },
        { para: '/modelos', rotulo: 'Modelos de mensagem' },
        { para: '/equipe', rotulo: 'Equipe do escritório' },
        { para: '/configuracoes', rotulo: 'Configurações' },
      ],
    },
  ];

  return (
    /*
     * `sticky top-0 h-dvh` e não a altura natural do flex.
     *
     * Sem a altura fixa, o trilho estica junto com o conteúdo — numa fila de
     * cobranças com dezenas de itens ele fica com milhares de pixels e rola para
     * fora da tela junto com a lista, deixando a colaboradora sem navegação no
     * meio do trabalho. Com `h-dvh`, ele ocupa exatamente uma tela e gruda no
     * topo; e o `overflow-y-auto` da lista de links abaixo finalmente entra em
     * ação quando o próprio menu não couber na altura da janela.
     */
    <nav
      className="sticky top-0 flex h-dvh w-64 shrink-0 flex-col bg-tinta-950 text-papel-300"
      aria-label="Navegação principal"
    >
      {/* A marca, no preto que ela foi desenhada para ocupar. */}
      <div className="border-b border-tinta-800 px-6 py-7">
        <img
          src="/marca/marca-empilhada.png"
          alt="Bianca Ávila Advocacia"
          className="w-full max-w-[168px]"
          width={800}
          height={565}
        />
      </div>

      <div className="flex-1 overflow-y-auto py-5">
        {grupos.map((grupo) => (
          <div key={grupo.titulo} className="mb-6">
            <h2 className="px-6 pb-2 font-sans text-[0.6875rem] font-medium uppercase tracking-[0.14em] text-tinta-500">
              {grupo.titulo}
            </h2>
            <ul>
              {grupo.itens.map((item) => (
                <li key={item.para}>
                  <NavLink
                    to={item.para}
                    className={({ isActive }) =>
                      [
                        'flex items-center justify-between gap-2 py-2.5 pr-4 pl-6 text-sm transition-colors',
                        // A barra dourada na aresta marca o item ativo sem precisar
                        // pintar o fundo inteiro de dourado, o que gritaria.
                        isActive
                          ? 'border-l-2 border-ouro-500 bg-tinta-900 pl-[22px] font-medium text-ouro-300'
                          : 'border-l-2 border-transparent text-papel-400 hover:bg-tinta-900 hover:text-papel-200',
                      ].join(' ')
                    }
                  >
                    <span>{item.rotulo}</span>
                    {item.contagem !== undefined && item.contagem > 0 && (
                      <span
                        className={[
                          'tabular rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold',
                          item.alerta
                            ? 'bg-vencido text-white'
                            : 'bg-tinta-700 text-papel-300',
                        ].join(' ')}
                      >
                        {item.contagem > 999 ? '999+' : item.contagem}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-tinta-800 px-6 py-5">
        <p className="truncate text-sm font-medium text-papel-200">{usuaria?.nome}</p>
        <button
          type="button"
          onClick={() => void sair()}
          className="mt-1 text-xs text-tinta-500 transition-colors hover:text-ouro-400"
        >
          Encerrar sessão
        </button>
      </div>
    </nav>
  );
}
